// Copyright 2018-2024 the Deno authors. All rights reserved. MIT license.
// This module is browser compatible.
import type { SemVer } from "./types.ts";

function formatNumber(value: number) {
  return value.toFixed(0);
}

/**
 * Format a SemVer object into a string.
 *
 * @example Usage
 * ```ts
 * import { format } from "@std/semver/format";
 * import { assertEquals } from "@std/assert";
 *
 * const semver = {
 *   major: 1,
 *   minor: 2,
 *   patch: 3,
 * };
 * assertEquals(format(semver), "1.2.3");
 * ```
 *
 * @param version The SemVer to format
 * @returns The string representation of a semantic version.
 */
export function format(version: SemVer): string {
  const major = formatNumber(version.major);
  const minor = formatNumber(version.minor);
  const patch = formatNumber(version.patch);
  const pre = version.prerelease?.join(".") ?? "";
  const build = version.build?.join(".") ?? "";

  const primary = `${major}.${minor}.${patch}`;
  const release = [primary, pre].filter((v) => v).join("-");
  return [release, build].filter((v) => v).join("+");
}

// denoCacheMetadata={"headers":{"via":"1.1 google","etag":"\"fb145b69df7d3c638903da6fbc4b8470\"","content-security-policy":"default-src 'none'; script-src 'none'; style-src 'none'; img-src 'none'; font-src 'none'; connect-src 'none'; frame-src 'none'; object-src 'none'; frame-ancestors 'none'; sandbox; form-action 'none';","x-jsr-cache-status":"hit","content-length":"1075","x-robots-tag":"noindex","alt-svc":"h3=\":443\"; ma=2592000,h3-29=\":443\"; ma=2592000","x-jsr-cache-id":"IAD","expires":"Sat, 08 Nov 2025 09:52:34 GMT","x-goog-storage-class":"STANDARD","server":"UploadServer","x-goog-stored-content-length":"1075","accept-ranges":"bytes","age":"1047","x-goog-metageneration":"1","last-modified":"Wed, 04 Sep 2024 10:41:41 GMT","access-control-expose-headers":"*","cross-origin-resource-policy":"cross-origin","x-content-type-options":"nosniff","x-goog-hash":"crc32c=ZCK++g==,md5=+xRbad99PGOJA9pvvEuEcA==","access-control-allow-origin":"*","content-type":"text/typescript","x-goog-stored-content-encoding":"identity","date":"Fri, 08 Nov 2024 09:52:34 GMT","x-goog-generation":"1725446501934672","cache-control":"public, max-age=31536000, immutable","x-guploader-uploadid":"AHmUCY2PXbC02yPbJuRmnDIkkORdkCTfrPQrmsbURbysFxWjPNNjrf83riVVYnab5HKCEs0W0aXcmaxszQ"},"url":"https://jsr.io/@std/semver/1.0.3/format.ts","time":1731060601}